// src/quiz/gre-mock3/quantitativeData.js
// GRE Mock 3 — Quantitative Reasoning (harder set).
// Section 1: 12 questions (21 min). Section 2: 15 questions (26 min).
// Question format: see ../greQuestionTypes.js
import { QC_OPTIONS } from "../greQuestionTypes";

const QC_QUESTION = "Compare Quantity A and Quantity B, using additional information centered above the two quantities if such information is given.";
const [QC_A, QC_B, QC_EQUAL, QC_UNKNOWN] = QC_OPTIONS;

const SALES_TABLE = {
  caption: "Quarterly Sales of Company Q by Region (in thousands of dollars)",
  headers: ["Region", "Q1", "Q2", "Q3", "Q4"],
  rows: [
    ["North", "240", "300", "270", "330"],
    ["South", "180", "165", "210", "245"],
    ["East", "150", "180", "195", "175"],
    ["West", "210", "195", "225", "250"],
  ],
};

// ================= SECTION 1 =================

export const section1Questions = [
  {
    id: 1,
    question: QC_QUESTION,
    given: "x and y are positive integers, and x² − y² = 45.",
    quantityA: "x",
    quantityB: "7",
    options: QC_OPTIONS,
    answer: QC_UNKNOWN,
    explanation: "(x − y)(x + y) = 45 with both factors positive and of the same parity: 1 × 45 gives x = 23, 3 × 15 gives x = 9, 5 × 9 gives x = 7. x can equal 7 or exceed it, so the relationship cannot be determined.",
  },
  {
    id: 2,
    question: QC_QUESTION,
    quantityA: "3⁴⁰",
    quantityB: "4³⁰",
    options: QC_OPTIONS,
    answer: QC_A,
    explanation: "Rewrite with a common exponent: 3⁴⁰ = (3⁴)¹⁰ = 81¹⁰ and 4³⁰ = (4³)¹⁰ = 64¹⁰. Since 81 > 64, Quantity A is greater.",
  },
  {
    id: 3,
    question: QC_QUESTION,
    given: "In triangle ABC, AB = 5 and BC = 8.",
    quantityA: "AC",
    quantityB: "13",
    options: QC_OPTIONS,
    answer: QC_B,
    explanation: "By the triangle inequality, AC < AB + BC = 13 (strictly, since a triangle cannot be degenerate). So Quantity B is greater even though AC's exact value is unknown.",
  },
  {
    id: 4,
    question: QC_QUESTION,
    given: "k is the sum of all the integers from −20 to 22, inclusive.",
    quantityA: "k",
    quantityB: "43",
    options: QC_OPTIONS,
    answer: QC_EQUAL,
    explanation: "The integers from −20 to 20 cancel in pairs, leaving 21 + 22 = 43.",
  },
  {
    id: 5,
    question: "How many positive integers less than 1,000 are divisible by neither 6 nor 15?",
    options: ["780", "790", "800", "810", "833"],
    answer: "800",
    explanation: "From 1 to 999: multiples of 6 = 166, of 15 = 66, of both (lcm 30) = 33. By inclusion–exclusion, 166 + 66 − 33 = 199 are divisible by 6 or 15, so 999 − 199 = 800 are divisible by neither.",
  },
  {
    id: 6,
    question: "The operation ⊕ is defined by a ⊕ b = (a + b)/(1 + ab) for all numbers a and b with ab ≠ −1. What is the value of (1/2 ⊕ 1/3) ⊕ 1/4?",
    options: ["7/9", "9/11", "11/13", "13/15", "1"],
    answer: "9/11",
    explanation: "1/2 ⊕ 1/3 = (5/6)/(7/6) = 5/7. Then 5/7 ⊕ 1/4 = (27/28)/(33/28) = 27/33 = 9/11.",
  },
  {
    id: 7,
    type: "numeric",
    question: "A 40-liter mixture is 25 percent alcohol by volume. How many liters of pure alcohol must be added so that the resulting mixture is 40 percent alcohol by volume?",
    answer: 10,
    explanation: "The mixture contains 10 L of alcohol. Adding x liters: (10 + x)/(40 + x) = 0.4, so 10 + x = 16 + 0.4x, 0.6x = 6, x = 10.",
  },
  {
    id: 8,
    type: "multi",
    question: "If x is a positive integer and x³ − x is divisible by 4, which of the following could be the value of x?",
    options: ["2", "3", "5", "6", "7", "10"],
    answer: ["3", "5", "7"],
    explanation: "x³ − x = (x − 1)x(x + 1). If x is odd, x − 1 and x + 1 are consecutive even numbers, so the product is divisible by 8. If x is even, x − 1 and x + 1 are odd, so x itself must be divisible by 4; 2, 6 and 10 are not (6 → 210, 10 → 990).",
  },
  {
    id: 9,
    question: "A circle is inscribed in a square, and a smaller square is inscribed in the circle. The area of the region inside the circle but outside the smaller square is what fraction of the area of the larger square?",
    options: ["(π − 2)/4", "(π − 2)/2", "(4 − π)/4", "1/2 − π/8", "(π − 1)/4"],
    answer: "(π − 2)/4",
    explanation: "Let the large square have side 2 (area 4). The circle has radius 1 (area π). The small square has diagonal 2, so its area is 2²/2 = 2. The region has area π − 2, which is (π − 2)/4 of the large square.",
  },
  {
    id: 10,
    table: SALES_TABLE,
    question: "For how many of the four regions did sales increase from each quarter to the next, from Q1 through Q4?",
    options: ["0", "1", "2", "3", "4"],
    answer: "0",
    explanation: "Each region has at least one decline: North (Q2→Q3: 300→270), South (Q1→Q2: 180→165), East (Q3→Q4: 195→175), West (Q1→Q2: 210→195).",
  },
  {
    id: 11,
    type: "numeric",
    table: SALES_TABLE,
    question: "By what percent did the company's total sales increase from Q1 to Q4? Give your answer to the nearest whole percent. (Enter the number only.)",
    answer: 28,
    explanation: "Q1 total = 240 + 180 + 150 + 210 = 780. Q4 total = 330 + 245 + 175 + 250 = 1,000. (1,000 − 780)/780 ≈ 28.2%, i.e. 28%.",
  },
  {
    id: 12,
    type: "multi",
    table: SALES_TABLE,
    question: "Which of the regions accounted for more than 25 percent of the company's total annual sales?",
    options: ["North", "South", "East", "West"],
    answer: ["North"],
    explanation: "Annual totals: North 1,140, South 800, East 700, West 880; overall 3,520. 25% of 3,520 = 880. Only North exceeds it; West equals 880 exactly, which is not more than 25%.",
  },
];

// ================= SECTION 2 =================

export const section2Questions = [
  {
    id: 13,
    question: QC_QUESTION,
    given: "0 < a < b < 1",
    quantityA: "a/b",
    quantityB: "(a + 1)/(b + 1)",
    options: QC_OPTIONS,
    answer: QC_B,
    explanation: "(a + 1)/(b + 1) − a/b = (b − a)/(b(b + 1)) > 0 because b > a. Adding the same positive amount to the numerator and denominator of a fraction less than 1 moves it closer to 1.",
  },
  {
    id: 14,
    question: QC_QUESTION,
    quantityA: "The number of ways to choose 4 people from a group of 10",
    quantityB: "The number of ways to choose 6 people from a group of 10",
    options: QC_OPTIONS,
    answer: QC_EQUAL,
    explanation: "Choosing 4 to include is the same as choosing 6 to exclude: C(10, 4) = C(10, 6) = 210.",
  },
  {
    id: 15,
    question: QC_QUESTION,
    given: "A list of numbers has mean 10 and standard deviation 2. Each number in the list is multiplied by 3, and then 4 is subtracted from each result.",
    quantityA: "The standard deviation of the new list",
    quantityB: "6",
    options: QC_OPTIONS,
    answer: QC_EQUAL,
    explanation: "Multiplying by 3 multiplies the standard deviation by 3 (to 6); subtracting 4 shifts every value equally and does not change the spread.",
  },
  {
    id: 16,
    question: QC_QUESTION,
    given: "x ≠ 0",
    quantityA: "(x² + 1)/x",
    quantityB: "2",
    options: QC_OPTIONS,
    answer: QC_UNKNOWN,
    explanation: "x = 1 gives 2 (equal); x = 2 gives 2.5 (A greater); x = −1 gives −2 (B greater). The inequality x + 1/x ≥ 2 holds only for positive x.",
  },
  {
    id: 17,
    question: QC_QUESTION,
    given: "A rectangular box has edges of length 2, 3 and 6.",
    quantityA: "The length of the longest line segment that fits inside the box",
    quantityB: "7",
    options: QC_OPTIONS,
    answer: QC_EQUAL,
    explanation: "The longest segment is the space diagonal: √(2² + 3² + 6²) = √49 = 7.",
  },
  {
    id: 18,
    question: "If x and y are integers and 2ˣ · 3ʸ = 6⁵/4, what is the value of x + y?",
    options: ["6", "7", "8", "9", "10"],
    answer: "8",
    explanation: "6⁵/4 = 2⁵·3⁵/2² = 2³·3⁵. By unique prime factorization, x = 3 and y = 5, so x + y = 8.",
  },
  {
    id: 19,
    question: "Three fair coins are tossed and a fair six-sided die is rolled. What is the probability that the number of heads equals the number shown on the die?",
    options: ["1/8", "7/48", "1/6", "7/24", "3/16"],
    answer: "7/48",
    explanation: "The die shows 1–6 and the heads count is 0–3, so a match needs 1, 2 or 3 heads. P(1, 2 or 3 heads) = 3/8 + 3/8 + 1/8 = 7/8, and the die must show that particular number with probability 1/6: 7/8 × 1/6 = 7/48.",
  },
  {
    id: 20,
    type: "numeric",
    question: "Machine A produces 120 widgets per hour and Machine B produces 80 widgets per hour. Machine A starts at 9:00 a.m. and Machine B starts at 10:30 a.m.; both then run continuously. How many hours after 9:00 a.m. will the two machines have produced a combined total of 1,000 widgets?",
    answer: 5.6,
    explanation: "By 10:30 a.m. (1.5 hours) A has made 180 widgets. The remaining 820 are produced at 200 per hour, taking 4.1 hours. Total: 1.5 + 4.1 = 5.6 hours.",
  },
  {
    id: 21,
    question: "The sum of the first n positive odd integers is n². What is the sum of all the odd integers from 51 to 99, inclusive?",
    options: ["1,825", "1,850", "1,875", "1,900", "2,500"],
    answer: "1,875",
    explanation: "The odd integers 1 to 99 are the first 50 odd numbers (sum 2,500); 1 to 49 are the first 25 (sum 625). 2,500 − 625 = 1,875.",
  },
  {
    id: 22,
    question: "Line ℓ passes through the point (2, 5) and is perpendicular to the line 3x − 4y = 12. What is the y-intercept of line ℓ?",
    options: ["7/3", "17/3", "23/3", "8", "26/3"],
    answer: "23/3",
    explanation: "3x − 4y = 12 has slope 3/4, so ℓ has slope −4/3. y − 5 = −4/3 (x − 2) gives y = −4/3 x + 8/3 + 5 = −4/3 x + 23/3.",
  },
  {
    id: 23,
    type: "multi",
    question: "If n is an integer and 2ⁿ > n⁴, which of the following could be the value of n?",
    options: ["−1", "0", "1", "2", "16", "17"],
    answer: ["0", "1", "17"],
    explanation: "n = −1: 1/2 > 1 is false. n = 0: 1 > 0 true. n = 1: 2 > 1 true. n = 2: 4 > 16 false. n = 16: 2¹⁶ = 65,536 = 16⁴, not greater. n = 17: 131,072 > 83,521 true.",
  },
  {
    id: 24,
    type: "numeric",
    question: "The average (arithmetic mean) of five consecutive even integers is 3 times the smallest of the five. What is the largest of the five integers?",
    answer: 10,
    explanation: "Let the integers be a, a + 2, …, a + 8. Their average is a + 4 = 3a, so a = 2 and the largest is a + 8 = 10.",
  },
  {
    id: 25,
    question: "If the radius of a right circular cylinder is increased by 20 percent and its height is decreased by 20 percent, by what percent does its volume change?",
    options: ["−4%", "0%", "+4%", "+15.2%", "+20%"],
    answer: "+15.2%",
    explanation: "V = πr²h, so the new volume is (1.2)² × 0.8 = 1.44 × 0.8 = 1.152 times the original: an increase of 15.2%. The radius counts twice because it is squared.",
  },
  {
    id: 26,
    question: "A list of 9 different positive integers has a median of 20 and an average (arithmetic mean) of 20. What is the greatest possible value of the largest integer in the list?",
    options: ["64", "76", "84", "90", "99"],
    answer: "84",
    explanation: "The sum is 9 × 20 = 180. To maximize the largest value, minimize the rest: the four below the median are 1, 2, 3, 4 (sum 10), the median is 20, and the next three are 21, 22, 23 (sum 66). Largest = 180 − 10 − 20 − 66 = 84.",
  },
  {
    id: 27,
    type: "numeric",
    question: "What is the remainder when 2⁵⁰ is divided by 7?",
    answer: 4,
    explanation: "2³ = 8 leaves remainder 1 when divided by 7, so powers of 2 repeat every 3. 50 = 3 × 16 + 2, so 2⁵⁰ = (2³)¹⁶ · 2² leaves remainder 1 · 4 = 4.",
  },
];
